import type { ReactElement } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import type { Role } from '@/types';

export interface ProtectedRouteProps {
  children: ReactElement;
  /** If set, only these roles may access. */
  roles?: Role[];
  /** If true, block users who already have a profile (used by /onboarding). */
  requireNoProfile?: boolean;
  /** If true, block users who don't have a profile yet. */
  requireProfile?: boolean;
}

export function ProtectedRoute({ children, roles, requireNoProfile, requireProfile }: ProtectedRouteProps) {
  const { user, isAuthed, isBootstrapping } = useAuth();
  const loc = useLocation();

  if (isBootstrapping) {
    return (
      <div
        role="status"
        aria-live="polite"
        style={{ padding: '48px 16px', textAlign: 'center' }}
      >
        <span className="text-muted text-body">لحظة...</span>
      </div>
    );
  }

  if (!isAuthed || !user) {
    const next = encodeURIComponent(loc.pathname + loc.search);
    return <Navigate to={`/auth?next=${next}`} replace />;
  }

  if (roles && !roles.includes(user.role)) {
    // Land the user on a page that makes sense for their role instead of
    // silently bouncing to "/" (which looks like "nothing happened").
    const fallback = user.role === 'admin'
      ? '/admin'
      : user.role === 'host'
        ? '/seekers'
        : '/hosts';
    return <Navigate to={fallback} replace />;
  }

  if (requireNoProfile && user.hasProfile) {
    return <Navigate to="/" replace />;
  }

  if (requireProfile && !user.hasProfile) {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
}

export default ProtectedRoute;
